(() => {
  if (window.IndigoReviewBrowser) {
    window.IndigoReviewBrowser.initialize(document);
    return;
  }
  const pageSize = 5;
  const datasets = new Map();
  const httpsUrl = (value) => {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && !url.username && !url.password ? url.href : null;
    } catch {
      return null;
    }
  };
  const normalizeReviews = (payload) => {
    if (!payload || !Array.isArray(payload.reviews)) throw new Error('Invalid review dataset');
    return payload.reviews.map((row, index) => {
      if (!row || !Number.isInteger(Number(row.rating)) || Number(row.rating) < 1 || Number(row.rating) > 5) throw new Error('Invalid review rating');
      const author = typeof row.author === 'string' ? row.author : '';
      const body = typeof row.body === 'string' ? row.body : '';
      const createdAt = typeof row.createdAt === 'string' ? row.createdAt : '';
      return {
        id: String(row.id ?? index),
        author,
        body,
        rating: Number(row.rating),
        createdAt,
        timestamp: Date.parse(createdAt) || 0,
        verified: row.verified === true,
        incentivized: row.incentivized === true,
        images: Array.isArray(row.images) ? row.images.map(httpsUrl).filter(Boolean) : [],
        videos: Array.isArray(row.videos) ? row.videos.map((video) => ({ url: httpsUrl(video?.url), poster: httpsUrl(video?.poster) })).filter((video) => video.url) : [],
        reply: typeof row.reply === 'string' ? row.reply : '',
        search: `${author}\n${body}`.toLocaleLowerCase(),
        index
      };
    });
  };
  const getReviews = (url) => {
    if (!datasets.has(url)) {
      const request = fetch(url, { credentials: 'omit', cache: 'force-cache' })
        .then((response) => {
          if (!response.ok) throw new Error('Review request failed');
          return response.json();
        })
        .then(normalizeReviews)
        .catch((error) => {
          datasets.delete(url);
          throw error;
        });
      datasets.set(url, request);
    }
    return datasets.get(url);
  };
  const createElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  const initializeSection = (root) => {
    if (root.dataset.reviewsInitialized) return;
    root.dataset.reviewsInitialized = 'true';
    const labels = JSON.parse(root.dataset.reviewLabels);
    const format = (key, values = {}) => (labels[key] || '').replace(/\{([a-z_]+)\}/g, (match, name) => values[name] === undefined ? match : String(values[name]));
    const locale = document.documentElement.lang || 'en';
    const integerFormat = new Intl.NumberFormat(locale);
    const averageFormat = new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 2 });
    const dateFormat = new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
    const status = root.querySelector('[data-review-status]');
    const summary = root.querySelector('[data-review-summary]');
    const toolbar = root.querySelector('[data-review-toolbar]');
    const list = root.querySelector('[data-review-list]');
    const ratingSelect = root.querySelector('[data-review-rating-select]');
    const sortSelect = root.querySelector('[data-review-sort]');
    const searchInput = root.querySelector('[data-review-search]');
    const clearButton = root.querySelector('[data-review-clear]');
    const moreButton = root.querySelector('[data-review-more]');
    const errorBox = root.querySelector('[data-review-error]');
    const ratingButtons = [...root.querySelectorAll('[data-review-rating]')];
    let reviews = [];
    let matching = [];
    let shown = 0;
    let loading = false;
    let loaded = false;
    let disposed = false;
    let searchTimer;
    let observer;
    const makeStars = (rating) => {
      const stars = createElement('div', 'indigo-reviews__stars');
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', format('rating_label', { rating }));
      const symbols = createElement('span', '', '★'.repeat(rating) + '☆'.repeat(5 - rating));
      symbols.setAttribute('aria-hidden', 'true');
      stars.append(symbols);
      return stars;
    };
    const makeCard = (review) => {
      const card = createElement('article', 'indigo-reviews__card');
      card.tabIndex = -1;
      card.dataset.reviewId = review.id;
      const top = createElement('div', 'indigo-reviews__card-top');
      const author = createElement('div');
      if (review.author) author.append(createElement('h3', 'indigo-reviews__author', review.author));
      if (review.verified) author.append(createElement('span', 'indigo-reviews__verified', labels.verified));
      if (review.incentivized) author.append(createElement('span', 'indigo-reviews__incentivized', labels.incentivized));
      top.append(author);
      if (review.timestamp) {
        const date = createElement('time', 'indigo-reviews__date', dateFormat.format(new Date(review.timestamp)));
        date.dateTime = review.createdAt;
        top.append(date);
      }
      card.append(top, makeStars(review.rating));
      if (review.body) card.append(createElement('p', 'indigo-reviews__body', review.body));
      if (review.images.length || review.videos.length) {
        const media = createElement('div', 'indigo-reviews__media');
        review.images.forEach((url) => {
          const link = createElement('a');
          link.href = url;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.setAttribute('aria-label', labels.photo_open);
          const image = createElement('img', 'indigo-reviews__photo');
          image.src = url;
          image.alt = labels.photo_alt;
          image.loading = 'lazy';
          image.decoding = 'async';
          link.append(image);
          media.append(link);
        });
        review.videos.forEach((attachment) => {
          const video = createElement('video', 'indigo-reviews__video', labels.video_fallback);
          video.src = attachment.url;
          if (attachment.poster) video.poster = attachment.poster;
          video.controls = true;
          video.preload = 'none';
          video.playsInline = true;
          video.setAttribute('aria-label', labels.video_label);
          media.append(video);
        });
        card.append(media);
      }
      if (review.reply) {
        const reply = createElement('div', 'indigo-reviews__reply');
        reply.append(createElement('span', 'indigo-reviews__reply-title', labels.reply_label), createElement('p', '', review.reply));
        card.append(reply);
      }
      return card;
    };
    const updateResults = () => {
      status.textContent = matching.length ? format('results_count', { shown: integerFormat.format(shown), total: integerFormat.format(matching.length) }) : reviews.length ? labels.no_results : labels.no_reviews;
      moreButton.hidden = shown >= matching.length;
      clearButton.hidden = ratingSelect.value === 'all' && !searchInput.value.trim();
      ratingButtons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.reviewRating === ratingSelect.value)));
    };
    const appendPage = (moveFocus = false) => {
      const nextPage = matching.slice(shown, shown + pageSize);
      const fragment = document.createDocumentFragment();
      let firstCard;
      nextPage.forEach((review) => {
        const card = makeCard(review);
        if (!firstCard) firstCard = card;
        fragment.append(card);
      });
      list.append(fragment);
      shown += nextPage.length;
      updateResults();
      if (moveFocus && firstCard) firstCard.focus({ preventScroll: true });
    };
    const filterReviews = () => {
      if (!loaded) return;
      clearTimeout(searchTimer);
      const query = searchInput.value.trim().toLocaleLowerCase();
      const rating = Number(ratingSelect.value);
      matching = reviews.filter((review) => (!rating || review.rating === rating) && (!query || review.search.includes(query)));
      matching.sort((left, right) => {
        const recent = right.timestamp - left.timestamp || left.index - right.index;
        if (sortSelect.value === 'oldest') return left.timestamp - right.timestamp || left.index - right.index;
        if (sortSelect.value === 'highest') return right.rating - left.rating || recent;
        if (sortSelect.value === 'lowest') return left.rating - right.rating || recent;
        return recent;
      });
      list.querySelectorAll('video').forEach((video) => video.pause());
      list.replaceChildren();
      shown = 0;
      appendPage();
    };
    const renderSummary = () => {
      const total = reviews.length;
      const distribution = [0, 0, 0, 0, 0, 0];
      let score = 0;
      reviews.forEach((review) => {
        distribution[review.rating] += 1;
        score += review.rating;
      });
      const average = total ? score / total : 0;
      const averageText = total ? averageFormat.format(average) : '—';
      root.querySelector('[data-review-average]').textContent = averageText;
      root.querySelector('[data-review-average-stars]').setAttribute('aria-label', total ? format('average_label', { rating: averageText }) : labels.no_reviews);
      root.querySelector('[data-review-average-fill]').style.width = `${average / 5 * 100}%`;
      root.querySelector('[data-review-total]').textContent = format(total === 1 ? 'review_one' : 'review_many', { count: integerFormat.format(total) });
      ratingButtons.forEach((button) => {
        const rating = Number(button.dataset.reviewRating);
        const count = distribution[rating];
        button.querySelector('[data-review-rating-count]').textContent = integerFormat.format(count);
        button.querySelector('[data-review-rating-bar]').style.width = `${total ? count / total * 100 : 0}%`;
        button.setAttribute('aria-label', format('filter_rating', { rating, count: integerFormat.format(count) }));
      });
      summary.hidden = false;
      toolbar.hidden = !total;
    };
    const load = async () => {
      if (loading || loaded || disposed) return;
      loading = true;
      errorBox.hidden = true;
      status.textContent = labels.loading;
      list.setAttribute('aria-busy', 'true');
      try {
        reviews = await getReviews(root.dataset.reviewsUrl);
        if (disposed) return;
        loaded = true;
        renderSummary();
        filterReviews();
      } catch {
        if (disposed) return;
        status.textContent = '';
        errorBox.hidden = false;
      } finally {
        loading = false;
        if (!disposed) list.setAttribute('aria-busy', 'false');
      }
    };
    ratingSelect.addEventListener('change', filterReviews);
    sortSelect.addEventListener('change', filterReviews);
    searchInput.addEventListener('input', () => {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(filterReviews, 150);
    });
    clearButton.addEventListener('click', () => {
      ratingSelect.value = 'all';
      searchInput.value = '';
      filterReviews();
    });
    ratingButtons.forEach((button) => button.addEventListener('click', () => {
      ratingSelect.value = ratingSelect.value === button.dataset.reviewRating ? 'all' : button.dataset.reviewRating;
      filterReviews();
    }));
    moreButton.addEventListener('click', () => appendPage(true));
    root.querySelector('[data-review-retry]').addEventListener('click', load);
    const cleanup = (event) => {
      if (!event.target.contains(root)) return;
      disposed = true;
      clearTimeout(searchTimer);
      if (observer) observer.disconnect();
      list.querySelectorAll('video').forEach((video) => video.pause());
      document.removeEventListener('shopify:section:unload', cleanup);
    };
    document.addEventListener('shopify:section:unload', cleanup);
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        load();
      }, { rootMargin: '350px 0px' });
      observer.observe(root);
    } else load();
  };
  const initialize = (scope) => {
    scope.querySelectorAll('[data-indigo-reviews]').forEach(initializeSection);
  };
  window.IndigoReviewBrowser = { initialize };
  initialize(document);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
